import type { IntelligenceFrame } from "@/features/intelligence/api/frames";

export interface IntelligenceApprovalRequest {
  toolCallId: string;
  toolName: string | null;
  argsText: string | null;
  detail: string | null;
}

export function approvalRequestOf(frame: IntelligenceFrame): IntelligenceApprovalRequest | null {
  const data = parsed(frame);
  if (!data) return null;
  if (!namesApproval(frame, data)) return null;
  const sources = payloads(data);
  if (settledDecision(sources) !== null) return null;
  const toolCallId = firstString(sources, ["toolCallId", "tool_call_id"]);
  if (!toolCallId) return null;
  const card = cardOf(sources);
  return {
    toolCallId,
    toolName: firstString(sources, ["toolName", "tool_name", "toolCallName", "tool_call_name"]),
    argsText: argsTextOf(sources),
    detail: firstString(card ? [card, ...sources] : sources, [
      "effect",
      "detail",
      "message",
      "prompt",
      "question",
      "description",
      "reason",
    ]),
  };
}

export function unactionableApprovalFrame(frame: IntelligenceFrame): boolean {
  const data = parsed(frame);
  if (!data) return false;
  if (!namesApproval(frame, data)) return false;
  if (settledDecision(payloads(data)) !== null) return false;
  return approvalRequestOf(frame) === null;
}

export function approvalSettledBy(frame: IntelligenceFrame, toolCallId: string): boolean {
  const data = parsed(frame);
  if (!data) return false;
  const sources = payloads(data);
  if (firstString(sources, ["toolCallId", "tool_call_id"]) !== toolCallId) return false;
  if (namesApproval(frame, data) && settledDecision(sources) !== null) return true;
  return frame.event === "TOOL_CALL_RESULT" || frame.event === "TOOL_CALL_END";
}

export type ApprovalDeliveryOutcome = "delivered" | "unknown";

export function approvalDeliveryOutcome(
  delivered: boolean,
  confirmedEarlier = false,
): ApprovalDeliveryOutcome {
  if (delivered) return "delivered";
  return confirmedEarlier ? "delivered" : "unknown";
}

function parsed(frame: IntelligenceFrame): Record<string, unknown> | null {
  if (frame.data === "") return null;
  try {
    const body = JSON.parse(frame.data) as unknown;
    return body !== null && typeof body === "object" && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

const APPROVAL_TOKEN = "approval";

function namesApproval(frame: IntelligenceFrame, data: Record<string, unknown>): boolean {
  const event = frame.event ?? "";
  const name = typeof data.name === "string" ? data.name : "";
  return (
    event.toLowerCase().includes(APPROVAL_TOKEN) || name.toLowerCase().includes(APPROVAL_TOKEN)
  );
}

function payloads(data: Record<string, unknown>): Record<string, unknown>[] {
  const nested =
    data.value !== null && typeof data.value === "object" && !Array.isArray(data.value)
      ? (data.value as Record<string, unknown>)
      : null;
  return nested ? [nested, data] : [data];
}

function settledDecision(sources: Record<string, unknown>[]): boolean | null {
  for (const source of sources) {
    if (typeof source.approved === "boolean") return source.approved;
  }
  return null;
}

function firstString(sources: Record<string, unknown>[], keys: string[]): string | null {
  for (const source of sources) {
    for (const key of keys) {
      const v = source[key];
      if (typeof v === "string" && v !== "") return v;
    }
  }
  return null;
}

function cardOf(sources: Record<string, unknown>[]): Record<string, unknown> | null {
  for (const source of sources) {
    const card = source.card;
    if (card !== null && typeof card === "object" && !Array.isArray(card)) {
      return card as Record<string, unknown>;
    }
  }
  return null;
}

function argsTextOf(sources: Record<string, unknown>[]): string | null {
  for (const source of sources) {
    for (const key of ["args", "arguments", "toolCallArgs", "tool_call_args", "input"]) {
      const v = source[key];
      if (typeof v === "string" && v !== "") return v;
      if (v !== null && typeof v === "object") {
        try {
          return JSON.stringify(v, null, 2);
        } catch {}
      }
    }
  }
  return null;
}
